/**
 * DepartureDialog - Mark an aircraft as departed or schedule a future departure
 *
 * Movement ledger model: creates outbound record (+ paired inbound if return date given).
 * If departure date is in the future, shows both "Schedule Departure" and "Confirm Now" options.
 */

import { useState, useCallback } from 'react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { useCreateDepartureEvent } from '@/hooks/useAllocationEvents'
import { useCreateAircraftSchedule } from '@/hooks/useAircraftSchedules'

interface DepartureDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tailNumber: string
  allocationId: string
  aircraftId: string
  locationId: string
}

export function DepartureDialog({
  open,
  onOpenChange,
  tailNumber,
  allocationId,
  aircraftId,
  locationId,
}: DepartureDialogProps) {
  const [departureDate, setDepartureDate] = useState('')
  const [destinationIcao, setDestinationIcao] = useState('')
  const [destinationName, setDestinationName] = useState('')
  const [expectedReturn, setExpectedReturn] = useState('')
  const [confidence, setConfidence] = useState<'confirmed' | 'tentative'>('confirmed')
  const [notes, setNotes] = useState('')

  const [discardOpen, setDiscardOpen] = useState(false)

  const departureEvent = useCreateDepartureEvent()
  const createSchedule = useCreateAircraftSchedule()

  const isDirty =
    departureDate !== '' ||
    destinationIcao !== '' ||
    destinationName !== '' ||
    expectedReturn !== '' ||
    confidence !== 'confirmed' ||
    notes !== ''

  // Detect if departure date is in the future
  const isFutureDeparture = departureDate
    ? new Date(departureDate).getTime() > Date.now()
    : false

  const resetForm = useCallback(() => {
    setDepartureDate('')
    setDestinationIcao('')
    setDestinationName('')
    setExpectedReturn('')
    setConfidence('confirmed')
    setNotes('')
  }, [])

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && isDirty) {
        setDiscardOpen(true)
        return
      }
      if (!nextOpen) resetForm()
      onOpenChange(nextOpen)
    },
    [isDirty, onOpenChange, resetForm],
  )

  const handleDiscard = () => {
    setDiscardOpen(false)
    resetForm()
    onOpenChange(false)
  }

  const handleSchedule = async () => {
    if (!departureDate) {
      toast.error('Departure date is required for scheduling')
      return
    }
    try {
      // Create outbound schedule
      const outbound = await createSchedule.mutateAsync({
        aircraft_id: aircraftId,
        location_id: locationId,
        direction: 'outbound',
        scheduled_at: new Date(departureDate).toISOString(),
        source: 'csr_entered',
        confidence,
        status: 'scheduled',
        airport_icao: destinationIcao || undefined,
        airport_name: destinationName || undefined,
        notes: notes || undefined,
      })

      // If expected return, create paired inbound
      if (expectedReturn) {
        await createSchedule.mutateAsync({
          aircraft_id: aircraftId,
          location_id: locationId,
          direction: 'inbound',
          scheduled_at: new Date(expectedReturn).toISOString(),
          source: 'csr_entered',
          confidence,
          status: 'scheduled',
          pair_id: outbound.id,
          airport_icao: destinationIcao || undefined,
          airport_name: destinationName || undefined,
        })
      }

      toast.success(`Departure scheduled for ${tailNumber}`)
      resetForm()
      onOpenChange(false)
    } catch (err) {
      toast.error('Failed to schedule departure')
      console.error('Schedule departure error:', err)
    }
  }

  const handleConfirm = async () => {
    try {
      await departureEvent.mutateAsync({
        allocation_id: allocationId,
        aircraft_id: aircraftId,
        location_id: locationId,
        event_at: departureDate ? new Date(departureDate).toISOString() : null,
        expected_return_at: expectedReturn ? new Date(expectedReturn).toISOString() : null,
        destination_icao: destinationIcao || null,
        destination_name: destinationName || null,
        confidence,
        notes: notes || null,
      })
      toast.success(`${tailNumber} marked as departed`)
      resetForm()
      onOpenChange(false)
    } catch (err) {
      toast.error('Failed to record departure')
      console.error('Departure event error:', err)
    }
  }

  const isPending = departureEvent.isPending || createSchedule.isPending

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Mark {tailNumber} as Departed</DialogTitle>
            <DialogDescription>
              Record a temporary departure. The aircraft will show as &quot;Away&quot; on the map.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="dep-date" className="text-xs">Departure Date & Time</Label>
              <Input
                id="dep-date"
                type="datetime-local"
                value={departureDate}
                onChange={(e) => setDepartureDate(e.target.value)}
                className="h-8 text-sm"
              />
              <p className="text-[10px] text-muted-foreground">
                Leave blank for current time (now)
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="dep-icao" className="text-xs">Destination ICAO</Label>
                <Input
                  id="dep-icao"
                  placeholder="e.g. KLAX"
                  value={destinationIcao}
                  onChange={(e) => setDestinationIcao(e.target.value.toUpperCase())}
                  maxLength={4}
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dep-name" className="text-xs">Destination Name</Label>
                <Input
                  id="dep-name"
                  placeholder="e.g. Los Angeles Intl"
                  value={destinationName}
                  onChange={(e) => setDestinationName(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="dep-return" className="text-xs">Expected Return</Label>
              <Input
                id="dep-return"
                type="datetime-local"
                value={expectedReturn}
                onChange={(e) => setExpectedReturn(e.target.value)}
                className="h-8 text-sm"
              />
              <p className="text-[10px] text-muted-foreground">
                Leave blank if return time is unknown
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Confidence</Label>
              <div className="flex gap-3">
                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input
                    type="radio"
                    name="dep-confidence"
                    checked={confidence === 'confirmed'}
                    onChange={() => setConfidence('confirmed')}
                    className="w-3 h-3"
                  />
                  Confirmed
                </label>
                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input
                    type="radio"
                    name="dep-confidence"
                    checked={confidence === 'tentative'}
                    onChange={() => setConfidence('tentative')}
                    className="w-3 h-3"
                  />
                  Tentative
                </label>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="dep-notes" className="text-xs">Notes</Label>
              <Textarea
                id="dep-notes"
                placeholder="Optional notes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="text-sm min-h-[60px]"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className={isFutureDeparture ? 'flex-col gap-2 sm:flex-col' : ''}>
            {isFutureDeparture ? (
              <div className="flex gap-2 w-full">
                <Button
                  onClick={handleSchedule}
                  disabled={isPending}
                  className="flex-1"
                >
                  {createSchedule.isPending ? 'Scheduling...' : 'Schedule Departure'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleConfirm}
                  disabled={isPending}
                  className="flex-1"
                >
                  {departureEvent.isPending ? 'Recording...' : 'Confirm Now'}
                </Button>
              </div>
            ) : (
              <Button onClick={handleConfirm} disabled={isPending}>
                {departureEvent.isPending ? 'Recording...' : 'Confirm Departure'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discard confirmation */}
      <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved departure details. Are you sure you want to discard them?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleDiscard}>Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
